import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, User, Mail, Phone, Trash2, Edit, X, UserPlus, Save, Megaphone, CheckSquare, Square, Eye, Send  } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function PatientsPage() {
  const [patients, setPatients] = useState([])
  const navigate = useNavigate()
  const[loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [pathologyTags, setPathologyTags] = useState([])
  const[offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  // Modales
  const [selectedPatient, setSelectedPatient] = useState(null)
  const[editData, setEditData] = useState({})
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [newData, setNewData] = useState({ first_name: '', last_name: '', gender: '', email: '', phone: '', address: '', notes: '', birth_date: '', ss_number: '', main_motive: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const[reasons, setReasons] = useState([])

  // --- NOUVEAU : LOGIQUE CRM & CAMPAGNES ---
  const[selectedIds, setSelectedIds] = useState(new Set())
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false)
  const [campaignTemplates, setCampaignTemplates] = useState([])
  const [libraryFiles, setLibraryFiles] = useState([])
  
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [selectedFileId, setSelectedFileId] = useState('')
  const [sendingCampaign, setSendingCampaign] = useState(false)

  useEffect(() => {
    api.get('/api/admin/reasons').then(res => setReasons(res.data)).catch(console.error)
    api.get('/api/patients/tags').then(res => setPathologyTags(res.data)).catch(console.error)
  },[])

  const fetchPatients = (searchTerm = '', currentOffset = 0, append = false) => {
    if(!append) setLoading(true)
    api.get('/api/patients/', { params: { search: searchTerm, offset: currentOffset, limit: 15 } })
      .then(res => {
          if (append) setPatients(prev => [...prev, ...res.data])
          else setPatients(res.data)
          setHasMore(res.data.length === 15)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setOffset(0)
      fetchPatients(search, 0, false)
    }, 300)
    return () => clearTimeout(delayDebounceFn)
  }, [search])

  const handleLoadMore = () => {
      const newOffset = offset + 9
      setOffset(newOffset)
      fetchPatients(search, newOffset, true)
  }

  // --- ACTIONS CRM ---
  const toggleSelection = (id) => {
      const newSet = new Set(selectedIds)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      setSelectedIds(newSet)
  }

  const toggleSelectAll = () => {
      if (selectedIds.size === patients.length) {
          setSelectedIds(new Set()) // Tout désélectionner
      } else {
          const newSet = new Set(patients.map(p => p.id))
          setSelectedIds(newSet) // Tout sélectionner (les visibles)
      }
  }

  const openCampaignModal = async () => {
      try {
          const [tplRes, filesRes] = await Promise.all([
              api.get('/api/library/documents'),
              api.get('/api/library/files')
          ])
          // On ne garde que les modèles SMS et Email
          setCampaignTemplates(tplRes.data.filter(t => t.category.startsWith('campaign_')))
          setLibraryFiles(filesRes.data)
          setSelectedTemplateId('')
          setSelectedFileId('')
          setIsCampaignModalOpen(true)
      } catch(e) { toast.error("Erreur de chargement") }
  }

  const handleSendCampaign = async () => {
      if(!selectedTemplateId) return toast.error("Sélectionnez un modèle")
      setSendingCampaign(true)
      try {
          const res = await api.post('/api/patients/campaign', {
              patient_ids: Array.from(selectedIds),
              template_id: parseInt(selectedTemplateId),
              file_id: selectedFileId ? parseInt(selectedFileId) : null
          })
          toast.success(res.data.message)
          setIsCampaignModalOpen(false)
          setSelectedIds(new Set()) // On vide la sélection
      } catch(e) {
          toast.error("Erreur lors de l'envoi")
      } finally {
          setSendingCampaign(false)
      }
  }

  // --- NOUVEAU : TELECHARGEMENT CSV ---
  const handleExportCSV = async () => {
      const toastId = toast.loading("Génération de l'export...")
      try {
          const payload = {
              search: search,
              patient_ids: selectedIds.size > 0 ? Array.from(selectedIds) : null
          }
          
          const response = await api.post('/api/patients/export-crm', payload, { responseType: 'blob' })
          
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Export_CRM_${new Date().toISOString().split('T')[0]}.csv`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          
          toast.success("Export réussi !", { id: toastId })
      } catch (error) {
          toast.error("Erreur lors de l'export", { id: toastId })
      }
  }

  // L'APERÇU EN DIRECT (LE CERVEAU !)
  const getPreviewContent = () => {
      if (!selectedTemplateId) return null;
      const tpl = campaignTemplates.find(t => t.id === parseInt(selectedTemplateId))
      if (!tpl) return null;

      // On prend le premier patient de la sélection pour l'exemple
      const samplePatientId = Array.from(selectedIds)[0]
      const samplePatient = patients.find(p => p.id === samplePatientId)
      if (!samplePatient) return null;

      let text = tpl.content;
      text = text.replace(/{{nom}}/g, samplePatient.last_name || "");
      text = text.replace(/{{prenom}}/g, samplePatient.first_name || "");
      text = text.replace(/{{civilite}}/g, samplePatient.gender === 'F' ? "Mme" : samplePatient.gender === 'M' ? "M." : "");
      text = text.replace(/{{date_jour}}/g, new Date().toLocaleDateString('fr-FR'));

      if (text.includes('{{fichier_joint}}')) {
           if (selectedFileId) {
                const file = libraryFiles.find(f => f.id === parseInt(selectedFileId));
                const link = `${window.location.origin}/api/library/files/p/${file.public_token}`;
                const replacement = tpl.category === 'campaign_email' ? `<a href="${link}" style="color: blue; text-decoration: underline;">${file?.name || 'Fichier'}</a>` : link;
                text = text.replace(/{{fichier_joint}}/g, replacement);
           } else {
                text = text.replace(/{{fichier_joint}}/g, "<span style='color: red; font-weight: bold;'>[FICHIER MANQUANT]</span>");
           }
      }
      return { text, isEmail: tpl.category === 'campaign_email' }
  }


  // --- ACTIONS PATIENT (Création / Mise à jour) ---
  const handleCreate = async (e) => {
      e.preventDefault()
      setIsSubmitting(true)
      try {
          const payload = { ...newData }
          Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null })
          const res = await api.post('/api/patients/', payload)
          toast.success("Nouveau patient créé !")
          setIsCreateModalOpen(false)
          navigate(`/admin/patients/${res.data.id}`)
      } catch (error) { toast.error("Erreur de création") } 
      finally { setIsSubmitting(false) }
  }

  const selectedTemplateObj = campaignTemplates.find(t => t.id === parseInt(selectedTemplateId));
  const requiresFile = selectedTemplateObj?.content.includes('{{fichier_joint}}');

  return (
    <div className="space-y-6 pb-24"> {/* pb-24 pour ne pas cacher la barre flottante */}
      {/* 1 Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
            <h1 className="text-2xl font-serif text-gray-800">Patients & CRM</h1>
            <p className="text-gray-500 mt-1">Gérez vos dossiers et vos campagnes de fidélisation.</p>
        </div>
        <div className="flex gap-2">
            <button 
                onClick={handleExportCSV} 
                className="btn bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm text-sm"
                title={selectedIds.size > 0 ? "Exporter les patients sélectionnés" : "Exporter tous les résultats"}
            >
                ⬇️ Exporter {selectedIds.size > 0 ? `(${selectedIds.size})` : '(CSV)'}
            </button>
            <button onClick={() => setIsCreateModalOpen(true)} className="btn btn-primary flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> Nouveau Patient
            </button>
        </div>
      </div>


      {/* 2. Barre de recherche et Filtres Rapides */}
      <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative w-full md:flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Recherche par nom, pathologie, téléphone, confrère, prescipteur..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10 w-full h-11 bg-gray-50 border-transparent focus:bg-white focus:border-sage-400 rounded-lg"
                    autoFocus
                />
            </div>
            {/* BOUTON SELECT ALL */}
            <button 
                onClick={toggleSelectAll} 
                className={`h-11 px-4 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shrink-0 ${selectedIds.size === patients.length && patients.length > 0 ? 'bg-sage-100 text-sage-700 border border-sage-200' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
            >
                {selectedIds.size === patients.length && patients.length > 0 ? <CheckSquare className="w-4 h-4"/> : <Square className="w-4 h-4"/>}
                Tout cocher
            </button>
          </div>
          
          {/* NOUVEAU : TAGS CLINIQUES (PILLS) */}
          {pathologyTags.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  <span className="text-[10px] font-bold text-gray-400 uppercase shrink-0">Tags rapides :</span>
                  {pathologyTags.map(tag => (
                      <button
                          key={tag}
                          onClick={() => setSearch(tag)}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-colors whitespace-nowrap border ${
                              search === tag ? 'bg-sage-600 text-white border-sage-600 shadow-sm' : 'bg-sage-50 text-sage-600 border-sage-200 hover:bg-sage-100'
                          }`}
                      >
                          {tag}
                      </button>
                  ))}
              </div>
          )}
      </div>


      {/* 3. Liste */}
      {loading ? (
        <div className="flex justify-center items-center h-64"><div className="spinner" /></div>
      ) : (
        <>
        {patients.length === 0 ? (
          <div className="card text-center py-12">
            <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucun patient trouvé</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {patients.map(patient => (
              <div 
                key={patient.id} 
                onClick={() => navigate(`/admin/patients/${patient.id}`)}
                className={`card hover:shadow-md transition-all cursor-pointer border-2 relative overflow-hidden ${selectedIds.has(patient.id) ? 'border-sage-400 bg-sage-50/30' : 'border-transparent hover:border-sage-200'}`}
              >
                {/* CASE À COCHER ABSOLUE */}
                <div 
                    className="absolute top-3 right-3 z-10 p-2"
                    onClick={(e) => { e.stopPropagation(); toggleSelection(patient.id); }}
                >
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedIds.has(patient.id) ? 'bg-sage-600 border-sage-600' : 'bg-white border-gray-300'}`}>
                        {selectedIds.has(patient.id) && <CheckSquare className="w-4 h-4 text-white" />}
                    </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 shadow-inner mt-1">
                    <span className="text-sage-700 font-bold text-lg font-serif">
                      {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 pr-8">
                    <p className="font-bold text-gray-800 truncate text-lg">
                      {patient.first_name} {patient.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-1.5">
                      <Phone className="w-3.5 h-3.5" /> {patient.phone}
                    </p>
                    {patient.email && (
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                        <Mail className="w-3.5 h-3.5" /> {patient.email}
                        </p>
                    )}
                    {patient.pathology && (
                        <p className="text-xs font-medium text-red-600 mt-2 truncate bg-red-50 px-2 py-0.5 rounded w-fit">
                            {patient.pathology}
                        </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {hasMore && !loading && (
            <div className="flex justify-center mt-6">
                <button onClick={handleLoadMore} className="btn bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shadow-sm">
                    Charger plus de résultats...
                </button>
            </div>
        )}
        </>
      )}

      {/* BARRE FLOTTANTE D'ACTION CRM */}
      {selectedIds.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.1)] z-40 animate-in slide-in-from-bottom-full">
              <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                      <div className="bg-sage-100 text-sage-700 font-black text-xl w-10 h-10 flex items-center justify-center rounded-full">
                          {selectedIds.size}
                      </div>
                      <span className="font-bold text-gray-700">Patient(s) sélectionné(s)</span>
                  </div>
                  <div className="flex gap-3 w-full sm:w-auto">
                      <button onClick={() => setSelectedIds(new Set())} className="btn bg-gray-100 text-gray-600 flex-1 sm:flex-none">Désélectionner</button>
                      <button onClick={openCampaignModal} className="btn btn-primary flex items-center justify-center gap-2 flex-1 sm:flex-none shadow-lg">
                          <Megaphone className="w-4 h-4"/> Créer une campagne
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* MODALE D'ENVOI DE CAMPAGNE (AVEC APERÇU) */}
      {isCampaignModalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col h-[90vh]">
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
                      <h3 className="font-serif font-bold text-xl text-gray-800 flex items-center gap-2">
                          <Megaphone className="w-5 h-5 text-orange-500" /> Paramétrer la campagne
                      </h3>
                      <button onClick={() => setIsCampaignModalOpen(false)}><X className="w-6 h-6 text-gray-400 hover:text-gray-600"/></button>
                  </div>

                  <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                      {/* COLONNE GAUCHE : REGLAGES */}
                      <div className="w-full md:w-1/2 p-6 border-r border-gray-100 overflow-y-auto bg-white">
                          <p className="text-sm text-gray-500 mb-6">Vous vous apprêtez à envoyer un message à <strong className="text-sage-600">{selectedIds.size} patient(s)</strong>.</p>
                          
                          <div className="space-y-5">
                              <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">1. Choisir le modèle (Bibliothèque)</label>
                                  <select 
                                    value={selectedTemplateId} 
                                    onChange={e => setSelectedTemplateId(e.target.value)}
                                    className="w-full bg-gray-50 border-gray-200 py-3"
                                  >
                                      <option value="">Sélectionnez un message...</option>
                                      {campaignTemplates.map(t => (
                                          <option key={t.id} value={t.id}>
                                              {t.category === 'campaign_sms' ? '📱 SMS - ' : '✉️ Email - '} {t.name}
                                          </option>
                                      ))}
                                  </select>
                              </div>

                              {requiresFile && (
                                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl animate-in slide-in-from-top-2">
                                      <label className="block text-xs font-bold text-blue-800 uppercase mb-2">2. Fichier à joindre à ce message</label>
                                      <select 
                                        value={selectedFileId} 
                                        onChange={e => setSelectedFileId(e.target.value)}
                                        className="w-full bg-white border-blue-200 text-blue-900 py-2.5"
                                      >
                                          <option value="">Sélectionnez un fichier public (Recette, Guide...)...</option>
                                          {libraryFiles.filter(f => f.is_public).map(f => (
                                              <option key={f.id} value={f.id}>{f.name}</option>
                                          ))}
                                      </select>
                                      <p className="text-[10px] text-blue-600 mt-2">Un lien sécurisé de téléchargement sera inséré à la place de la variable {"{{fichier_joint}}"}.</p>
                                  </div>
                              )}
                          </div>
                      </div>

                      {/* COLONNE DROITE : L'APERÇU EN DIRECT */}
                      <div className="w-full md:w-1/2 p-6 overflow-y-auto bg-gray-50">
                          <h4 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5 mb-4">
                              <Eye className="w-4 h-4"/> Aperçu avant envoi
                          </h4>
                          
                          {getPreviewContent() ? (
                              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                                  {getPreviewContent().isEmail ? (
                                      <div 
                                          className="text-sm text-gray-700 font-sans leading-relaxed" 
                                          dangerouslySetInnerHTML={{ __html: getPreviewContent().text }} 
                                      />
                                  ) : (
                                      <div className="text-sm text-gray-700 font-mono whitespace-pre-wrap">
                                          {getPreviewContent().text.replace(/<[^>]*>?/gm, '')} {/* Nettoyage HTML pour SMS */}
                                      </div>
                                  )}
                                  <div className="mt-4 pt-3 border-t border-gray-100 text-[10px] text-gray-400 italic">
                                      Aperçu généré avec le dossier de : {patients.find(p => p.id === Array.from(selectedIds)[0])?.first_name} {patients.find(p => p.id === Array.from(selectedIds)[0])?.last_name}.
                                  </div>
                              </div>
                          ) : (
                              <div className="h-full flex items-center justify-center text-sm text-gray-400 italic border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                                  Sélectionnez un modèle à gauche pour voir à quoi ressemblera votre message.
                              </div>
                          )}
                      </div>
                  </div>

                  {/* FOOTER */}
                  <div className="p-4 border-t border-gray-100 bg-white rounded-b-2xl flex justify-end gap-3">
                      <button onClick={() => setIsCampaignModalOpen(false)} className="btn bg-gray-100 text-gray-600 hover:bg-gray-200 px-6">Annuler</button>
                      <button 
                        onClick={handleSendCampaign} 
                        disabled={sendingCampaign || !selectedTemplateId || (requiresFile && !selectedFileId)} 
                        className="btn btn-primary px-8 shadow-lg flex items-center gap-2"
                      >
                          {sendingCampaign ? <div className="spinner w-4 h-4 border-white"/> : <><Send className="w-4 h-4"/> Lancer la campagne</>}
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* MODALE CREATION PATIENT */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
                    <h2 className="text-xl font-serif font-bold text-gray-800 flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-sage-600" />
                        Nouveau Patient
                    </h2>
                    <button onClick={() => setIsCreateModalOpen(false)} className="p-1.5 hover:bg-gray-200 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleCreate} className="p-6 space-y-4">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-4">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Sexe</label>
                            <select value={newData.gender || ''} onChange={e => setNewData({...newData, gender: e.target.value})} className="w-full text-sm py-2.5">
                                <option value="">-</option>
                                <option value="F">Femme</option>
                                <option value="M">Homme</option>
                            </select>
                        </div>
                        <div className="col-span-4">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prénom *</label>
                            <input type="text" required value={newData.first_name} onChange={e => setNewData({...newData, first_name: e.target.value})} className="w-full" autoFocus/>
                        </div>
                        <div className="col-span-4">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nom *</label>
                            <input type="text" required value={newData.last_name} onChange={e => setNewData({...newData, last_name: e.target.value})} className="w-full" />
                        </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase">Nom de jeune fille</label>
                      <input value={newData.maiden_name || ''} onChange={e => setNewData({...newData, maiden_name: e.target.value})} className="w-full mt-1" placeholder="Optionnel" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Téléphone *</label>
                            <input type="tel" required value={newData.phone} onChange={e => setNewData({...newData, phone: e.target.value})} className="w-full" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date de naissance</label>
                            <input type="date" value={newData.birth_date} onChange={e => setNewData({...newData, birth_date: e.target.value})} className="w-full" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                        <input type="email" value={newData.email} onChange={e => setNewData({...newData, email: e.target.value})} className="w-full" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">N° Sécu</label>
                        <input type="text" value={newData.ss_number} onChange={e => setNewData({...newData, ss_number: e.target.value})} className="w-full font-mono text-sm" placeholder="1 80 05..." />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Motif principal</label>
                        <select value={newData.main_motive || ''} onChange={e => setNewData({...newData, main_motive: e.target.value})} className="w-full text-sm py-2.5">
                            <option value="">Non défini</option>
                            {reasons.map(r => (
                                <option key={r.id} value={r.name}>{r.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-red-500 uppercase">Pathologies / Contexte</label>
                      <textarea 
                          value={newData.pathology || ''} 
                          onChange={e => setNewData({...newData, pathology: e.target.value})} 
                          className="w-full mt-1 bg-red-50 border-red-200 focus:bg-white text-sm" 
                          placeholder="Ex: Stéatose hépatique, Obésité grade I..."
                          rows={2}
                      />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Médecin prescripteur</label>
                        <input type="text" value={newData.prescribing_doctor || ''} onChange={e => setNewData({...newData, prescribing_doctor: e.target.value})} className="w-full text-sm" placeholder="Dr. Dupont" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Adresse</label>
                        <textarea rows={2} value={newData.address} onChange={e => setNewData({...newData, address: e.target.value})} className="w-full" />
                    </div>

                    <div className="pt-4 flex gap-3 border-t border-gray-100">
                        <button type="button" onClick={() => setIsCreateModalOpen(false)} className="btn bg-white border border-gray-300 text-gray-600 flex-1 hover:bg-gray-50">
                            Annuler
                        </button>
                        <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1 flex items-center justify-center gap-2">
                            {isSubmitting ? <div className="spinner w-4 h-4 border-white" /> : <><Save className="w-4 h-4" /> Créer le dossier</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  )
}